import { Permission } from '@act/shared/authorization';
import { AddressDto, FeedItemDto } from '@act/shared/data-transfer-objects';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Information } from 'react-components/dialog-forms/components/InformationView';
import Loader from 'react-components/workflows/loader';

import { ContentPreview, InformationView, TaggableChip } from '@act/storybook';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { memberTransferRequestSelectors } from 'redux/slices/memberTransferRequestSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';

interface FeedMemberTransferRequestCardProps {
  feedItem: FeedItemDto;
  memberTransferRequestId: number;
  shouldHighlight: boolean;
  additionalDialogs?: { [key: string]: (...args: unknown[]) => void };
}

const FeedMemberTransferRequestCard = ({
  feedItem,
  memberTransferRequestId,
  additionalDialogs,
  shouldHighlight,
}: FeedMemberTransferRequestCardProps) => {
  let content: React.ReactElement;
  let chip: React.ReactElement;
  const menuItems: AdditionalFeedCardActions[] = [];

  const memberTransferRequest =
    memberTransferRequestSelectors.useSelectMemberTransferRequestById(
      memberTransferRequestId
    );

  const receivingGuide = userAppSelectors.useSelectUserById(
    memberTransferRequest?.receivingGuideId
  );

  const member = membersAppSelectors.useSelectMemberById(
    memberTransferRequest?.memberId
  );

  const canAuditTransferRequest = membersAppSelectors.useHasPermissionOnMember(
    member?.id,
    Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS
  );

  const transferRequestReason =
    memberTransferRequestSelectors.useSelectMemberTransferReasonById(
      memberTransferRequest?.transferReasonId
    );

  // Memoize the information data with stable string dependencies
  // MUST be called before any conditional returns to satisfy Rules of Hooks
  const informationData = useMemo<Information[]>(() => {
    // Handle null case early to prevent errors
    if (!memberTransferRequest) {
      return [];
    }

    const detailFields = {
      address: 'numericDetail',
      date: 'timestampDetail',
      string: 'stringDetail',
    };

    function constructAddress(address: AddressDto): string {
      return `${address?.line1}, ${
        address?.line2 ? address?.line2 + ', ' : ''
      }${address?.city}, ${address?.state} ${address?.zip}`;
    }

    let detailValue;

    if (transferRequestReason?.detailType === 'address') {
      detailValue = constructAddress(
        member?.addresses?.find(
          a => a.id === +memberTransferRequest?.numericDetail
        )
      );
    } else {
      detailValue =
        memberTransferRequest?.[
          detailFields[transferRequestReason?.detailType]
        ];

      if (transferRequestReason?.detailType === 'date') {
        detailValue = detailValue
          ? DateTime.fromMillis(detailValue)?.toLocaleString(DateTime.DATE_FULL)
          : '';
      }
    }

    return [
      {
        label: 'Member',
        value: member?.name,
      },
      {
        label: 'Reason',
        value: transferRequestReason?.text,
      },
      {
        label: transferRequestReason?.detailLabel,
        value: detailValue,
      },
      {
        label: 'New guide',
        value: receivingGuide?.name,
      },
      {
        label: 'Comments',
        value: memberTransferRequest?.commentToReceivingGuide,
      },
    ];
  }, [
    memberTransferRequest,
    member?.name,
    member?.addresses,
    receivingGuide?.name,
    transferRequestReason?.text,
    transferRequestReason?.detailType,
    transferRequestReason?.detailLabel,
    memberTransferRequest?.numericDetail,
    memberTransferRequest?.stringDetail,
    memberTransferRequest?.timestampDetail,
    memberTransferRequest?.commentToReceivingGuide,
  ]);

  if (!memberTransferRequest) {
    return <Loader />;
  }

  const isPending =
    memberTransferRequest?.approvedAt === null &&
    memberTransferRequest?.deniedAt === null;

  // Check if content needs collapse functionality (more than 2 items)
  const hasMoreInfo = informationData.length > 2;

  if (!isPending) {
    if (feedItem.action === 'audited') {
      if (!!memberTransferRequest.approvedAt) {
        chip = (
          <TaggableChip
            isTag
            color="success"
            variant="filled"
            label="Approved"
          />
        );
      } else {
        chip = (
          <TaggableChip
            isTag
            icon={<RemoveCircleIcon />}
            color="error"
            label="Declined"
          />
        );
      }
    }
  } else {
    if (canAuditTransferRequest) {
      menuItems.push({
        title: 'Audit member transfer',
        details: 'Audit member transfer',
        handler: () => {
          additionalDialogs?.openTransferDialog(memberTransferRequest);
        },
      });
    }
  }

  if (
    feedItem.action === 'created' &&
    memberTransferRequest.commentToReceivingGuide
  )
    content = (
      <>
        <Typography variant="subheadMedium">Creator Comment:</Typography>
        <br />
        <Typography variant="bodyLarge">
          {memberTransferRequest.commentToReceivingGuide}
        </Typography>
      </>
    );

  if (feedItem.action === 'audited' && memberTransferRequest.auditorComment)
    content = (
      <>
        <Typography variant="bodyMedium" component="p" display="block">
          Effective on{' '}
          {memberTransferRequest?.effectiveAt &&
            DateTime.fromMillis(
              memberTransferRequest?.effectiveAt
            )?.toLocaleString(DateTime.DATE_MED)}
        </Typography>
        <Typography variant="subheadMedium">Auditor Comment:</Typography>
        <br />
        <Typography variant="bodyLarge">
          {memberTransferRequest.auditorComment}
        </Typography>
      </>
    );
  return (
    <InfoCard
      shouldHighlight={shouldHighlight}
      variant="transfer"
      startTime={feedItem.createdAt}
      subject="Member Transfer Request"
      statusChips={[chip]}
      menuItems={menuItems}
    >
      <>
        {content}
        <Box sx={{ mt: content ? 2 : 0 }}>
          <ContentPreview showControls={hasMoreInfo}>
            <InformationView information={informationData} />
          </ContentPreview>
        </Box>
      </>
    </InfoCard>
  );
};

export default FeedMemberTransferRequestCard;
